import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';
import { safeRequest } from '../api/http';
import { setToken, getToken, removeToken } from '../utils/auth';

export const useAuth = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(!!getToken());
  
  // 获取当前用户信息
  const getCurrentUser = async () => {
    if (!getToken()) {
      setCurrentUser(null);
      setIsAuthenticated(false);
      return null;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // 使用安全请求，提供备用数据
      const userData = await safeRequest(
        () => API.auth.getUserProfile(),
        null,
        false // 不显示错误消息，由下面的setError处理
      );
      
      if (userData) {
        setCurrentUser(userData);
        setIsAuthenticated(true);
        return userData;
      } else {
        // API调用失败但不崩溃
        setError('Failed to get user information');
        return null;
      }
    } catch (error) {
      setError(error.message);
      if (error.response?.status === 401) {
        removeToken();
        setCurrentUser(null);
        setIsAuthenticated(false);
      }
      return null;
    } finally {
      setLoading(false);
    }
  };
  
  // 登录
  const login = async (credentials) => {
    try {
      setLoading(true);
      setError(null);

      // 使用安全请求
      const data = await safeRequest(
        () => API.auth.login(credentials),
        null,
        false // 不显示错误消息，由下面的setError处理
      );

      if (!data) {
        setError('Login failed, please try again later');
        return null;
      }

      const { token, refreshToken } = data;

      setToken(token);
      localStorage.setItem('refreshToken', refreshToken);

      await getCurrentUser();
      return data;
    } catch (error) {
      setError(error.message || 'Login failed, please try again later');
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  // 验证码登录
  const codeLogin = async (credentials) => {
    try {
      setLoading(true);
      setError(null);

      // 使用安全请求
      const data = await safeRequest(
        () => API.auth.codeLogin(credentials),
        null,
        false
      );

      if (!data) {
        setError('Login failed, please try again later');
        return null;
      }

      const { token, refreshToken } = data;

      setToken(token);
      localStorage.setItem('refreshToken', refreshToken);

      await getCurrentUser();
      return data;
    } catch (error) {
      setError(error.message || 'Login failed, please try again later');
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  // 注册
  const register = async (registerData) => {
    try {
      setLoading(true);
      setError(null);

      // 使用安全请求
      const data = await safeRequest(
        () => API.auth.register(registerData),
        null,
        false
      );

      if (!data) {
        setError('Registration failed, please try again later');
        return null;
      }

      const { token, refreshToken } = data;

      setToken(token);
      localStorage.setItem('refreshToken', refreshToken);

      await getCurrentUser();
      return data;
    } catch (error) {
      setError(error.message || 'Registration failed, please try again later');
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  // 登出
  const logout = async () => {
    try {
      await safeRequest(
        () => API.auth.logout(),
        null,
        false
      );
    } catch (error) {
      console.error('Logout failed', error);
    } finally {
      removeToken();
      localStorage.removeItem('refreshToken');
      setCurrentUser(null);
      setIsAuthenticated(false);
      navigate('/campaigns/invitations/login');
    }
  };
  
  // 发送注册验证码
  const sendRegisterCode = async (data) => {
    try {
      setLoading(true);
      setError(null);
      return await safeRequest(
        () => API.auth.sendRegisterCode(data),
        { success: false },
        true
      );
    } catch (error) {
      setError(error.message || 'Failed to send verification code');
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  // 发送登录验证码
  const sendLoginCode = async (data) => {
    try {
      setLoading(true);
      setError(null);
      return await safeRequest(
        () => API.auth.sendLoginCode(data),
        { success: false },
        true
      );
    } catch (error) {
      setError(error.message || 'Failed to send verification code');
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  // 刷新token
  const refreshAuthToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      const data = await safeRequest(
        () => API.auth.refreshToken(refreshToken),
        null,
        false
      );
      
      if (!data) {
        logout();
        return null;
      }
      
      const { token, refreshToken: newRefreshToken } = data;
      
      setToken(token);
      localStorage.setItem('refreshToken', newRefreshToken);
      return data;
    } catch (error) {
      logout();
      throw error;
    }
  };
  
  // 在组件挂载时自动获取用户信息
  useEffect(() => {
    if (getToken()) {
      getCurrentUser().catch(console.error);
    }
  }, []);
  
  return {
    loading,
    error,
    currentUser,
    isAuthenticated,
    login,
    codeLogin,
    register,
    logout,
    getCurrentUser,
    sendRegisterCode,
    sendLoginCode,
    refreshAuthToken
  };
}; 